import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, EyeOff, Eye, Package, X, Upload } from "lucide-react";
import { api, apiError, API_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { EmptyState } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot, StaggerItem } from "@/components/motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TYPES = [
  { id: "food", dari: "غذا", pashto: "خواړه" },
  { id: "drink", dari: "نوشیدنی", pashto: "څښاک" },
  { id: "dessert", dari: "دسر", pashto: "خوږه" },
];

const BACKEND_ORIGIN = API_BASE.replace(/\/api$/, "");

function resolveImageSrc(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("/api/")) return `${BACKEND_ORIGIN}${imageUrl}`;
  return imageUrl;
}

export default function MenuPage() {
  const { t, tr, money, lang } = useI18n();
  const [tab, setTab] = useState("items");
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formItem, setFormItem] = useState({
    name_dari: "", name_pashto: "", description_dari: "", description_pashto: "",
    image_url: "", price: 0, cost_price: 0, preparation_time: 15,
    category_id: "", type: "food", status: "available", recipe: [],
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [openCat, setOpenCat] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [formCat, setFormCat] = useState({ name_dari: "", name_pashto: "", sort_order: 0, status: "active" });

  const load = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/menu/items"),
        api.get("/menu/categories"),
        api.get("/inventory/items"),
      ]);
      setItems(r1.data);
      setCats(r2.data);
      setStockItems(r3.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = items
    .filter((it) => catFilter === "all" ? true : it.category_id === catFilter)
    .filter((it) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return it.name_dari.toLowerCase().includes(q) || it.name_pashto.toLowerCase().includes(q);
    });

  const saveItem = async () => {
    if (!formItem.category_id) { toast.error(lang === "pashto" ? "کټګوري وټاکئ" : "دسته انتخاب کنید"); return; }
    if (Number(formItem.price) < 0) { toast.error(t("error")); return; }
    try {
      const recipe = (formItem.recipe || [])
        .filter((r) => r.stock_item_id && Number(r.quantity) > 0)
        .map((r) => ({ stock_item_id: r.stock_item_id, quantity: Number(r.quantity) }));
      const payload = { ...formItem, price: Number(formItem.price), cost_price: Number(formItem.cost_price), preparation_time: Number(formItem.preparation_time), recipe };
      if (editItem) await api.put(`/menu/items/${editItem.id}`, payload);
      else await api.post("/menu/items", payload);
      toast.success(t("success"));
      setOpenItem(false); setEditItem(null);
      setFormItem({ name_dari: "", name_pashto: "", description_dari: "", description_pashto: "", image_url: "", price: 0, cost_price: 0, preparation_time: 15, category_id: "", type: "food", status: "available", recipe: [] });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadItemImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(lang === "pashto" ? "یوازې انځور پورته کړئ" : "لطفاً فقط تصویر انتخاب کنید");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(lang === "pashto" ? "انځور باید له ۵MB کم وي" : "حجم تصویر باید کمتر از ۵MB باشد");
      return;
    }

    setUploadingImage(true);
    try {
      const encoded = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const res = await api.post("/menu/items/image", {
        filename: file.name,
        content_type: file.type,
        data: encoded,
      });
      setFormItem((current) => ({ ...current, image_url: res.data.image_url }));
      toast.success(t("success"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploadingImage(false);
    }
  };

  const toggleAvail = async (it) => {
    try {
      await api.put(`/menu/items/${it.id}/status`, { status: it.status === "available" ? "unavailable" : "available" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delItem = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/menu/items/${id}`);
      toast.success(t("success"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveCat = async () => {
    try {
      const payload = { ...formCat, sort_order: Number(formCat.sort_order) };
      if (editCat) await api.put(`/menu/categories/${editCat.id}`, payload);
      else await api.post("/menu/categories", payload);
      toast.success(t("success"));
      setOpenCat(false); setEditCat(null);
      setFormCat({ name_dari: "", name_pashto: "", sort_order: 0, status: "active" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delCat = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/menu/categories/${id}`);
      toast.success(t("success"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("menu")}</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-200 p-1 rounded-xl">
          <TabsTrigger value="items" className="data-[state=active]:bg-[#071A33] data-[state=active]:text-white rounded-lg">{t("menu")}</TabsTrigger>
          <TabsTrigger value="cats" className="data-[state=active]:bg-[#071A33] data-[state=active]:text-white rounded-lg">{t("category")}</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="w-full h-11 pr-10 pl-4 rounded-xl border border-gray-200 focus:outline-none focus:border-[#071A33]" />
            </div>
            <RpSelect
              value={catFilter}
              onValueChange={setCatFilter}
              className="h-11 w-auto min-w-[10rem] text-sm"
              options={[
                { value: "all", label: t("allCategories") },
                ...cats.map((c) => ({ value: c.id, label: tr(c, "name") })),
              ]}
            />
            <button
              data-testid="add-menu-item"
              onClick={() => { setEditItem(null); setFormItem({ name_dari: "", name_pashto: "", description_dari: "", description_pashto: "", image_url: "", price: 0, cost_price: 0, preparation_time: 15, category_id: cats[0]?.id || "", type: "food", status: "available", recipe: [] }); setOpenItem(true); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]"
            >
              <Plus className="w-4 h-4" /> {t("addItem")}
            </button>
          </div>

          {filtered.length === 0 ? <EmptyState title={t("empty")} /> : (
            <StaggerRoot className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" delay={0.03}>
              {filtered.map((it, idx) => (
                <StaggerItem key={it.id} index={idx}>
                <div className="rp-card rp-card-hover overflow-hidden h-full">
                  <div className="aspect-[4/3] bg-gray-100 relative">
                    {it.image_url && <img src={resolveImageSrc(it.image_url)} alt="" className="w-full h-full object-cover" />}
                    {it.status === "unavailable" && (
                      <div className="absolute inset-0 bg-black/50 grid place-items-center text-white font-bold text-sm">{t("unavailable")}</div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-gray-900 truncate">{tr(it, "name")}</div>
                    <div className="text-xs text-gray-500">{cats.find(c => c.id === it.category_id) ? tr(cats.find(c => c.id === it.category_id), "name") : ""}</div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="text-base font-bold text-[#071A33] tabular">{money(it.price)}</div>
                      {it.recipe && it.recipe.length > 0 && (
                        <span title={lang === "pashto" ? "د ګودام لینک" : "متصل به انبار"} className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                          <Package className="w-3 h-3" />
                          {it.recipe.length}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <button onClick={() => toggleAvail(it)} className="flex-1 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs inline-flex items-center justify-center gap-1">
                        {it.status === "available" ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => { setEditItem(it); setFormItem({ ...it, recipe: it.recipe || [] }); setOpenItem(true); }} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => delItem(it.id)} className="p-1.5 rounded-lg border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
                </StaggerItem>
              ))}
            </StaggerRoot>
          )}
        </TabsContent>

        <TabsContent value="cats" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => { setEditCat(null); setFormCat({ name_dari: "", name_pashto: "", sort_order: 0, status: "active" }); setOpenCat(true); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]"
            >
              <Plus className="w-4 h-4" /> {t("addCategory")}
            </button>
          </div>
          <div className="rp-card overflow-hidden">
            <table className="w-full text-right">
              <thead className="bg-gray-50 text-sm text-gray-500">
                <tr><th className="p-4">{t("name")} (دری)</th><th>{t("name")} (پښتو)</th><th>{lang === "pashto" ? "ترتیب" : "ترتیب"}</th><th>{t("actions")}</th></tr>
              </thead>
              <tbody>
                {cats.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100">
                    <td className="p-4 text-gray-900 font-medium">{c.name_dari}</td>
                    <td className="p-4 text-gray-700">{c.name_pashto}</td>
                    <td className="p-4 tabular text-gray-500">{c.sort_order}</td>
                    <td className="p-4 flex gap-2">
                      <button onClick={() => { setEditCat(c); setFormCat({ name_dari: c.name_dari, name_pashto: c.name_pashto, sort_order: c.sort_order, status: c.status || "active" }); setOpenCat(true); }} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => delCat(c.id)} className="p-2 rounded-lg border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Item Dialog */}
      <Dialog open={openItem} onOpenChange={setOpenItem}>
        <DialogContent dir="rtl" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editItem ? t("edit") : t("addItem")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <label className="text-xs text-gray-500 mb-1 block">{t("name")} (دری)</label>
              <input value={formItem.name_dari} onChange={(e) => setFormItem({ ...formItem, name_dari: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("name")} (پښتو)</label>
              <input value={formItem.name_pashto} onChange={(e) => setFormItem({ ...formItem, name_pashto: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("category")}</label>
              <RpSelect
                allowEmpty
                value={formItem.category_id}
                onValueChange={(v) => setFormItem({ ...formItem, category_id: v })}
                placeholder="--"
                options={cats.map((c) => ({ value: c.id, label: tr(c, "name") }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("type")}</label>
              <RpSelect
                value={formItem.type}
                onValueChange={(v) => setFormItem({ ...formItem, type: v })}
                options={TYPES.map((tp) => ({
                  value: tp.id,
                  label: lang === "pashto" ? tp.pashto : tp.dari,
                }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("price")} (AFN)</label>
              <input type="number" value={formItem.price} onChange={(e) => setFormItem({ ...formItem, price: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("costPrice")}</label>
              <input type="number" value={formItem.cost_price} onChange={(e) => setFormItem({ ...formItem, cost_price: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">{t("image")}</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="h-24 sm:w-36 rounded-xl border border-dashed border-gray-300 bg-gray-50 hover:bg-rose-50 hover:border-[#7F1020] cursor-pointer overflow-hidden grid place-items-center text-center text-xs text-gray-600">
                  {formItem.image_url ? (
                    <img src={resolveImageSrc(formItem.image_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="px-3 inline-flex flex-col items-center gap-1">
                      <Upload className="w-5 h-5" />
                      {lang === "pashto" ? "انځور پورته کړئ" : "آپلود تصویر"}
                    </span>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    disabled={uploadingImage}
                    onChange={(e) => uploadItemImage(e.target.files?.[0])}
                  />
                </label>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm font-mono text-gray-500 flex items-center truncate">
                    {uploadingImage
                      ? (lang === "pashto" ? "پورته کېږي..." : "در حال آپلود...")
                      : formItem.image_url || (lang === "pashto" ? "تر اوسه انځور نه دی ټاکل شوی" : "هنوز تصویری انتخاب نشده است")}
                  </div>
                  {formItem.image_url && (
                    <button
                      type="button"
                      onClick={() => setFormItem({ ...formItem, image_url: "" })}
                      className="text-xs text-rose-700 hover:text-rose-800"
                    >
                      {lang === "pashto" ? "انځور لرې کړئ" : "حذف تصویر"}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("prepTime")}</label>
              <input type="number" value={formItem.preparation_time} onChange={(e) => setFormItem({ ...formItem, preparation_time: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("status")}</label>
              <RpSelect
                value={formItem.status}
                onValueChange={(v) => setFormItem({ ...formItem, status: v })}
                options={[
                  { value: "available", label: t("available_") },
                  { value: "unavailable", label: t("unavailable") },
                ]}
              />
            </div>
          </div>

          {/* Recipe section */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-[#071A33]" />
                <h4 className="font-bold text-gray-900">{lang === "pashto" ? "ریسیپي (د ګودام پر اساس کسر)" : "ترکیب (کسر خودکار از انبار)"}</h4>
              </div>
              <button
                type="button"
                data-testid="add-recipe-line"
                onClick={() => setFormItem({ ...formItem, recipe: [...(formItem.recipe || []), { stock_item_id: stockItems[0]?.id || "", quantity: 0 }] })}
                className="text-xs px-3 py-1.5 rounded-lg bg-[#071A33]/10 text-[#071A33] hover:bg-[#071A33]/20 font-medium inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> {t("stockItem")}
              </button>
            </div>
            {(!formItem.recipe || formItem.recipe.length === 0) ? (
              <div className="text-xs text-gray-500 py-3 text-center bg-gray-50 rounded-xl">
                {lang === "pashto" ? "د ګودام د کسر لپاره مواد زیات کړئ" : "برای کسر خودکار، مواد اولیه را اضافه کنید"}
              </div>
            ) : (
              <div className="space-y-2">
                {formItem.recipe.map((r, idx) => {
                  const stock = stockItems.find((s) => s.id === r.stock_item_id);
                  return (
                    <div key={idx} className="flex items-center gap-2" data-testid={`recipe-row-${idx}`}>
                      <RpSelect
                        allowEmpty
                        value={r.stock_item_id}
                        onValueChange={(v) => {
                          const next = [...formItem.recipe];
                          next[idx] = { ...next[idx], stock_item_id: v };
                          setFormItem({ ...formItem, recipe: next });
                        }}
                        placeholder="--"
                        className="flex-1 h-10 text-sm"
                        options={stockItems.map((s) => ({
                          value: s.id,
                          label: `${tr(s, "name")} (${s.unit})`,
                        }))}
                      />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={r.quantity}
                        onChange={(e) => {
                          const next = [...formItem.recipe];
                          next[idx] = { ...next[idx], quantity: e.target.value };
                          setFormItem({ ...formItem, recipe: next });
                        }}
                        placeholder={t("quantity")}
                        className="w-24 h-10 px-3 rounded-lg border border-gray-200 text-sm tabular text-center"
                      />
                      {stock && <span className="text-xs text-gray-500 w-12">{stock.unit}</span>}
                      <button
                        type="button"
                        onClick={() => setFormItem({ ...formItem, recipe: formItem.recipe.filter((_, i) => i !== idx) })}
                        className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter className="flex-row-reverse">
            <button onClick={saveItem} data-testid="save-menu-item" className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white hover:bg-[#7F1020]">{t("save")}</button>
            <button onClick={() => setOpenItem(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={openCat} onOpenChange={setOpenCat}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editCat ? t("edit") : t("addCategory")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={formCat.name_dari} onChange={(e) => setFormCat({ ...formCat, name_dari: e.target.value })} placeholder={t("name") + " (دری)"} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input value={formCat.name_pashto} onChange={(e) => setFormCat({ ...formCat, name_pashto: e.target.value })} placeholder={t("name") + " (پښتو)"} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input type="number" value={formCat.sort_order} onChange={(e) => setFormCat({ ...formCat, sort_order: e.target.value })} placeholder="0" className="w-full h-11 px-3 rounded-xl border border-gray-200" />
          </div>
          <DialogFooter className="flex-row-reverse">
            <button onClick={saveCat} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white">{t("save")}</button>
            <button onClick={() => setOpenCat(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaggerRoot>
  );
}
